import { ColDef } from '@ag-grid-community/core';
import { SelectOption, TransactionStatusCode, TransactionStatusCodeMap } from '@tassat/common-definitions';
import { UserRecord, UserRole } from '@tassat/fred-definitions';
import { CollateralHistoryTableOptions } from '@tassat/fred-extended-definitions';

import { validateNumbersOnly } from 'Lib/validationHelpers';
import { IFeaturesContext } from 'SharedComponents/hooks/useFeatures';

export const getAdminCollateralHistoryColumnDefs = (opts: {
    features: IFeaturesContext;
    currentUser: UserRecord;
    currentRole: UserRole;
    tableOptions: CollateralHistoryTableOptions;
    getMappedColumn: (column: any) => any;
    getBalance?: (params: any) => any;
    adminCollateralTypeOptions?: (features: IFeaturesContext) => SelectOption[];
}): ColDef[] => {
    const {
        features,
        currentUser,
        currentRole,
        tableOptions,
        getMappedColumn
    } = opts;

    const columnDefs: ColDef[] = [
        {
            field: 'AuditTime',
            headerName: 'Date/Time',
            cellRenderer: 'dateCellRender',
            minWidth: 80,
            menuTabs: [],
            flex: 1
        },
        {
            field: 'Status',
            cellRenderer: 'tranStatus',
            cellRendererParams: {
                currentUser,
                currentRole
            },
            headerName: 'Status',
            headerComponent: tableOptions.SelectedFilter === 'none' ? 'externalFilterHeader' : '',
            headerComponentParams: tableOptions.SelectedFilter === 'none' ? { getMappedColumn } : {},
            filter: tableOptions.SelectedFilter === 'none' ? 'externalFilter' : '',
            filterParams: tableOptions.SelectedFilter === 'none' ? {
                getMappedColumn,
                inputType: 'select',
                selectOptions: transactionStatusOptions,
                hideSort: true
            } : {},
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'PledgingUsername',
            headerName: 'Posting Username',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'PledgingAccountName',
            headerName: 'Posting Account Name',
            menuTabs: [],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'PledgingFirmName',
            headerName: 'Posting Account Firm Name',
            menuTabs: [],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'PledgingAccountAddress',
            headerName: 'Posting Account Address',
            menuTabs: [],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'ReceivingAccountName',
            headerName: 'Receiving Account Name',
            menuTabs: [],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'ReceivingFirmName',
            headerName: 'Receiving Account Firm Name',
            menuTabs: [],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'ReceivingAccountAddress',
            headerName: 'Receiving Account Address',
            menuTabs: [],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'PledgeId',
            headerName: 'Post ID',
            cellRenderer: 'textCell',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'textEquals',
                validators: [validateNumbersOnly],
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'APIConnectionName',
            headerName: 'API Name',
            cellRenderer: 'multiLineText',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'text',
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'Amount',
            headerName: 'Current Post Amount',
            cellRenderer: 'formatAssetsAmount',
            cellRendererParams: { isCollateral: true },
            valueGetter: ({ data }) => ({ ...data }),
            menuTabs: [],
            flex: 1
        }
    ];

    return columnDefs;
};

export const getClientCollateralHistoryColumnDefs = (opts: {
    features: IFeaturesContext;
    currentUser: UserRecord;
    currentRole: UserRole;
    tableOptions: CollateralHistoryTableOptions;
    getMappedColumn: (column: any) => any;
}): ColDef[] => {
    const {
        features,
        currentUser,
        currentRole,
        tableOptions,
        getMappedColumn
    } = opts;

    const columnDefs: ColDef[] = [
        {
            field: 'AuditTime',
            headerName: 'Date/Time',
            cellRenderer: 'dateCellRender',
            minWidth: 80,
            menuTabs: [],
            flex: 1
        },
        {
            field: 'Status',
            cellRenderer: 'tranStatus',
            cellRendererParams: {
                currentUser,
                currentRole
            },
            headerName: 'Status',
            headerComponent: tableOptions.SelectedFilter === 'collateral' ? 'externalFilterHeader' : '',
            headerComponentParams: tableOptions.SelectedFilter === 'collateral' ? { getMappedColumn } : {},
            filter: tableOptions.SelectedFilter === 'collateral' ? 'externalFilter' : '',
            filterParams: tableOptions.SelectedFilter === 'collateral' ? {
                getMappedColumn,
                inputType: 'select',
                selectOptions: transactionStatusOptions,
                hideSort: true
            } : {},
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'Direction',
            headerName: 'Direction',
            menuTabs: [],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'CounterpartyAccountName',
            headerName: 'Counterparty Account Name',
            menuTabs: [],
            minWidth: 100,
            flex: 1
        },
        {
            field: 'PledgeId',
            headerName: 'Post ID',
            cellRenderer: 'textCell',
            headerComponent: 'externalFilterHeader',
            headerComponentParams: { getMappedColumn },
            filter: 'externalFilter',
            filterParams: {
                getMappedColumn,
                inputType: 'textEquals',
                validators: [validateNumbersOnly],
                hideSort: true
            },
            menuTabs: ['filterMenuTab'],
            flex: 1
        },
        {
            field: 'Amount',
            headerName: 'Current Post Amount',
            cellRenderer: 'formatAssetsAmount',
            cellRendererParams: { isCollateral: true },
            valueGetter: ({ data }) => ({ ...data }),
            menuTabs: [],
            flex: 1
        }
    ];

    return columnDefs;
};

export const getMappedColumn = (column) => {
    switch (column) {
        case 'Status':
            return 'Status';
        case 'PledgorUsername':
            return 'PledgorUsername';
        case 'PledgeId':
            return 'PledgeId';
        case 'APIConnectionName':
            return 'APIConnectionName';
        default:
            return column;
    }
};

export const transactionStatusOptions: SelectOption[] = [
    {
        label: 'Select an option...',
        value: ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.ACTIVE]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.ACTIVE]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.REJECTED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.REJECTED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.PENDING]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.PENDING]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.PROCESSING]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.PROCESSING]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.FAILED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.FAILED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.DECLINED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.DECLINED]?.displayValue ?? ''
    },
    {
        value: TransactionStatusCodeMap[TransactionStatusCode.COMPLETED]?.value ?? '',
        label: TransactionStatusCodeMap[TransactionStatusCode.COMPLETED]?.displayValue ?? ''
    }
];